import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiClient,
  login as apiLogin,
  loginWithEmailCode as apiLoginWithEmailCode,
  logout as apiLogout,
  register as apiRegister,
  requestLoginEmailCode as apiRequestLoginEmailCode,
  requestPurchaseEmailCode as apiRequestPurchaseEmailCode,
  requestRegisterEmailCode as apiRequestRegisterEmailCode,
  verifyPurchaseEmailCode as apiVerifyPurchaseEmailCode
} from "../api/client";
import { createWorkspace as apiCreateWorkspace } from "../api/collab";
import { tokenStore } from "./tokenStore";
import type { Account, PurchaseEmailCodeStartResponse, PurchaseVerifyResponse, RegisterPayload, Tokens, User } from "../types/api";

// The backend is selected by build mode, not by the user: the dev server
// (`bun ./index.html`, NODE_ENV=development) targets the local control plane;
// the production build (`NODE_ENV=production bun build`) targets the hosted
// backend. Bun statically replaces `process.env.NODE_ENV` at build time.
export function defaultBaseUrlForNodeEnv(nodeEnv = process.env.NODE_ENV): string {
  return nodeEnv === "production" ? "https://lin-back.s-j.ai" : "http://127.0.0.1:47832";
}

export const DEFAULT_BASE_URL = defaultBaseUrlForNodeEnv();

interface AuthState {
  ready: boolean;
  baseUrl: string;
  user: User | null;
  account: Account | null;
  /** Every workspace the user belongs to (owner + joined). */
  accounts: Account[];
  isAdmin: boolean;
  client: ApiClient | null;
  setBaseUrl: (url: string) => void;
  register: (payload: RegisterPayload, targetBaseUrl?: string) => Promise<void>;
  requestRegisterEmailCode: (email: string, inviteCode?: string, targetBaseUrl?: string) => Promise<void>;
  login: (email: string, password: string, targetBaseUrl?: string) => Promise<void>;
  requestLoginEmailCode: (email: string, targetBaseUrl?: string) => Promise<void>;
  loginWithEmailCode: (email: string, emailCode: string, targetBaseUrl?: string) => Promise<void>;
  requestPurchaseEmailCode: (email: string, targetBaseUrl?: string) => Promise<PurchaseEmailCodeStartResponse>;
  verifyPurchaseEmailCode: (email: string, emailCode: string, password?: string, targetBaseUrl?: string) => Promise<PurchaseVerifyResponse>;
  /** Switch the active workspace (must be one the user belongs to). */
  switchAccount: (accountId: string) => void;
  /** Re-fetch `/me`; optionally activate an account from that fresh response. */
  reloadAccounts: (activateAccountId?: string) => Promise<Account[]>;
  /** Create a new workspace the user owns and switch into it. */
  createWorkspace: (name: string, slug?: string) => Promise<Account>;
  signOut: () => Promise<void>;
}

const ACTIVE_ACCOUNT_KEY = "cc_active_account";

function readActiveAccountId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/** The persisted active workspace if the user still belongs to it, else `fallback`. */
function pickActiveAccount(accts: Account[], fallback: Account): Account {
  const saved = readActiveAccountId();
  return accts.find((a) => a.id === saved) ?? fallback;
}

export function requestedAccountFromReload(accts: Account[], accountId?: string): Account | null {
  if (!accountId) return null;
  const next = accts.find((candidate) => candidate.id === accountId);
  if (!next) throw new Error("requested account was not returned by /me");
  return next;
}

const AuthContext = createContext<AuthState | null>(null);

function normalizeBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, "");
  if (!trimmed) return DEFAULT_BASE_URL;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // No scheme typed (e.g. "127.0.0.1:47832"): default loopback/LAN hosts to http
  // and anything public to https, so a bare local backend address Just Works.
  const host = trimmed.split("/")[0];
  const isLocal =
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|::1)(:\d+)?$/i.test(host) ||
    /^10\.\d+\.\d+\.\d+/.test(host) ||
    /^192\.168\.\d+\.\d+/.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+/.test(host);
  return `${isLocal ? "http" : "https"}://${trimmed}`;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [baseUrl, setBaseUrlState] = useState(DEFAULT_BASE_URL);
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Tokens kept in a ref so the ApiClient always reads the latest without
  // re-instantiating on every rotation.
  const tokensRef = useRef<{ accessToken: string; refreshToken: string } | null>(null);

  const persistTokens = useCallback(async (tokens: Tokens) => {
    tokensRef.current = { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken };
    await tokenStore.save(tokens);
  }, []);

  const signOut = useCallback(async () => {
    const refresh = tokensRef.current?.refreshToken;
    if (refresh) await apiLogout(baseUrl, refresh);
    tokensRef.current = null;
    await tokenStore.clear();
    setUser(null);
    setAccount(null);
    setAccounts([]);
    setIsAdmin(false);
    try {
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    } catch {
      /* ignore */
    }
  }, [baseUrl]);

  const client = useMemo(() => {
    if (!user) return null;
    return new ApiClient({
      baseUrl,
      getAccess: () => tokensRef.current?.accessToken ?? "",
      getRefresh: () => tokensRef.current?.refreshToken ?? "",
      onTokens: persistTokens,
      onUnauthorized: () => void signOut()
    });
  }, [user, baseUrl, persistTokens, signOut]);

  // Switch the active workspace. The user's token works for any workspace they
  // belong to (the backend checks membership per request), so this is a pure
  // client-side re-scope — screens re-render against the new account id.
  const switchAccount = useCallback(
    (accountId: string) => {
      const next = accounts.find((a) => a.id === accountId);
      if (!next) return;
      setAccount(next);
      try {
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
      } catch {
        /* ignore */
      }
    },
    [accounts]
  );

  const reloadAccounts = useCallback(async (activateAccountId?: string): Promise<Account[]> => {
    if (!client) return [];
    const me = await client.me();
    const accts = me.accounts ?? [me.account];
    const next = requestedAccountFromReload(accts, activateAccountId);
    setAccounts(accts);
    setUser(me.user);
    setIsAdmin(me.isAdmin);
    if (next) {
      setAccount(next);
      try {
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, next.id);
      } catch {
        /* ignore */
      }
    }
    return accts;
  }, [client]);

  // Create a workspace the user owns and make it active. Sets the returned
  // account directly (no accounts-list lookup) to avoid a switch-before-reload
  // race, then persists it as the active workspace.
  const createWorkspace = useCallback(
    async (name: string, slug?: string): Promise<Account> => {
      if (!client) throw new Error("not authenticated");
      const { account: created } = await apiCreateWorkspace(client, name, slug);
      setAccounts((prev) => (prev.some((a) => a.id === created.id) ? prev : [...prev, created]));
      setAccount(created);
      try {
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, created.id);
      } catch {
        /* ignore */
      }
      return created;
    },
    [client]
  );

  // `url` is the backend this session authenticated against. Adopt it as the
  // active baseUrl and persist it so every later call — and the next reload —
  // talks to the same backend (not a stale default).
  const completeSignIn = useCallback(
    async (tokens: Tokens, nextUser: User, nextAccount: Account, url: string) => {
      await persistTokens(tokens);
      setBaseUrlState(url);
      await tokenStore.saveBaseUrl(url);
      try {
        const probe = new ApiClient({
          baseUrl: url,
          getAccess: () => tokensRef.current?.accessToken ?? "",
          getRefresh: () => tokensRef.current?.refreshToken ?? "",
          onTokens: persistTokens,
          onUnauthorized: () => {
            tokensRef.current = null;
          }
        });
        const me = await probe.me();
        const accts = me.accounts ?? [me.account];
        setUser(me.user);
        setAccounts(accts);
        setAccount(pickActiveAccount(accts, me.account));
        setIsAdmin(me.isAdmin);
      } catch {
        setUser(nextUser);
        setAccounts([nextAccount]);
        setAccount(nextAccount);
        setIsAdmin(false);
      }
    },
    [persistTokens]
  );

  const register = useCallback(
    async (payload: RegisterPayload, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      const { tokens, user: u, account: a } = await apiRegister(url, payload);
      await completeSignIn(tokens, u, a, url);
    },
    [baseUrl, completeSignIn]
  );

  const requestRegisterEmailCode = useCallback(
    async (email: string, inviteCode?: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      await apiRequestRegisterEmailCode(url, email, inviteCode);
    },
    [baseUrl]
  );

  const login = useCallback(
    async (email: string, password: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      const { tokens, user: u, account: a } = await apiLogin(url, email, password);
      await completeSignIn(tokens, u, a, url);
    },
    [baseUrl, completeSignIn]
  );

  const requestLoginEmailCode = useCallback(
    async (email: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      await apiRequestLoginEmailCode(url, email);
    },
    [baseUrl]
  );

  const loginWithEmailCode = useCallback(
    async (email: string, emailCode: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      const { tokens, user: u, account: a } = await apiLoginWithEmailCode(url, email, emailCode);
      await completeSignIn(tokens, u, a, url);
    },
    [baseUrl, completeSignIn]
  );

  const requestPurchaseEmailCode = useCallback(
    async (email: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      return apiRequestPurchaseEmailCode(url, email);
    },
    [baseUrl]
  );

  const verifyPurchaseEmailCode = useCallback(
    async (email: string, emailCode: string, password?: string, targetBaseUrl?: string) => {
      const url = targetBaseUrl !== undefined ? normalizeBaseUrl(targetBaseUrl) : baseUrl;
      const result = await apiVerifyPurchaseEmailCode(url, email, emailCode, password);
      if (!result.needsPassword) {
        await completeSignIn(result.tokens, result.user, result.account, url);
      }
      return result;
    },
    [baseUrl, completeSignIn]
  );

  const setBaseUrl = useCallback(
    (url: string) => {
      const next = normalizeBaseUrl(url);
      if (next === baseUrl) return;
      // Tokens + account are scoped to a specific backend. Switching backends
      // invalidates the current session, so clear it to force a fresh login and
      // avoid sending one backend's account id to another (→ "access denied").
      if (tokensRef.current) {
        tokensRef.current = null;
        void tokenStore.clear();
        setUser(null);
        setAccount(null);
        setAccounts([]);
        setIsAdmin(false);
      }
      setBaseUrlState(next);
    },
    [baseUrl]
  );

  // Rehydrate session on launch.
  useEffect(() => {
    (async () => {
      // Backend URL is fixed by build mode (DEFAULT_BASE_URL); ignore any base
      // URL persisted by older builds that exposed a user override.
      const saved = await tokenStore.load();
      if (saved) {
        tokensRef.current = saved;
        try {
          const probe = new ApiClient({
            baseUrl: DEFAULT_BASE_URL,
            getAccess: () => tokensRef.current?.accessToken ?? "",
            getRefresh: () => tokensRef.current?.refreshToken ?? "",
            onTokens: persistTokens,
            onUnauthorized: () => {
              tokensRef.current = null;
            }
          });
          const me = await probe.me();
          const accts = me.accounts ?? [me.account];
          setUser(me.user);
          setAccounts(accts);
          setAccount(pickActiveAccount(accts, me.account));
          setIsAdmin(me.isAdmin);
        } catch {
          tokensRef.current = null;
          await tokenStore.clear();
          setIsAdmin(false);
        }
      }
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthState = {
    ready,
    baseUrl,
    user,
    account,
    accounts,
    isAdmin,
    client,
    setBaseUrl,
    register,
    requestRegisterEmailCode,
    login,
    requestLoginEmailCode,
    loginWithEmailCode,
    requestPurchaseEmailCode,
    verifyPurchaseEmailCode,
    switchAccount,
    reloadAccounts,
    createWorkspace,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
